import React, { useMemo, useCallback } from "react";
import { Divider, Typography, Button } from "@material-ui/core";

const parseVcard = (vcard) => {
  if (!vcard) return { name: null, phones: [] };

  const lines = vcard.split(/\r?\n/);
  const phones = [];
  let name = null;

  lines.forEach((line) => {
    if (!line) return;
    const [rawKey, ...rest] = line.split(":");
    if (!rawKey || rest.length === 0) return;

    const key = rawKey.trim().toUpperCase();
    const value = rest.join(":").trim();

    if (!value) return;

    if (key.startsWith("FN")) {
      name = value;
      return;
    }

    if (key.startsWith("TEL") || /ITEM\d+\.TEL/.test(key)) {
      phones.push(value);
    }
  });

  return { name, phones };
};

// Mantém só dígitos. Se vier com "tel:+55 (27) 999..." etc, limpa.
const normalizePhone = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  // remove prefixos comuns tipo "TEL;..." ou "tel:"
  const v = s.replace(/^tel:/i, "").trim();
  const digits = v.replace(/\D+/g, "");
  return digits;
};

/**
 * Props novas:
 * - companyId: da conversa atual
 * - whatsappId: conexão (whatsapp) da conversa atual
 * - onConversar: callback para o pai abrir o NewTicketModal
 *
 * onConversar(payload):
 * {
 *   companyId, whatsappId,
 *   name, displayName,
 *   phoneRaw, phone // phone = só dígitos
 * }
 */
const VcardPreview = ({
  contacts = [],
  companyId,
  whatsappId,
  onConversar,
}) => {
  const parsedContacts = useMemo(
    () =>
      contacts.map((contact) => {
        const parsed = parseVcard(contact?.vcard);
        const displayName = contact?.displayName || "Contato";
        return {
          displayName,
          name: parsed.name || displayName,
          phones: parsed.phones,
        };
      }),
    [contacts]
  );

  const handleConversar = useCallback(
    (c, phoneRaw) => {
      const phone = normalizePhone(phoneRaw);

      if (!phone) return;

      if (typeof onConversar === "function") {
        onConversar({
          companyId,
          whatsappId,
          name: c?.name ?? null,
          displayName: c?.displayName ?? null,
          phoneRaw,
          phone,
        });
      }
    },
    [companyId, whatsappId, onConversar]
  );

  if (!parsedContacts.length) return null;

  return (
    <div
      style={{
        minWidth: "250px",
        padding: "12px",
        borderRadius: "8px",
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        marginBottom: "8px",
      }}
    >
      {parsedContacts.map((contact, index) => (
        <div key={`${contact.displayName}-${index}`}>
          <Typography variant="subtitle1" color="primary">
            {contact.name}
          </Typography>

          {contact.phones.length > 0 ? (
            contact.phones.map((phoneRaw, phoneIndex) => {
              const phoneDigits = normalizePhone(phoneRaw);

              return (
                <div
                  key={`${phoneRaw}-${phoneIndex}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginTop: 4,
                  }}
                >
                  <Typography variant="body2">{phoneRaw}</Typography>

                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={!phoneDigits}
                    onClick={() => handleConversar(contact, phoneRaw)}
                  >
                    Conversar
                  </Button>
                </div>
              );
            })
          ) : (
            <Typography variant="body2" color="textSecondary">
              sem detalhes do contato
            </Typography>
          )}

          {index < parsedContacts.length - 1 && (
            <Divider style={{ margin: "12px 0" }} />
          )}
        </div>
      ))}
    </div>
  );
};

export default VcardPreview;